import { ContractPosition } from '../../types'
import { PlanPositionRow } from '../../../Plans/Positions/Edit/types'
import { PurchaseLotItem } from '../../../Purchases/types'

const DEFAULT_COUNTRY_ORIGIN = {
    country: [
        {
            name: 'Российская Федерация',
            digital_code: '643',
        },
    ],
}

const DEFAULT_COUNTRY_MANUFACTURER = {
    name: 'Российская Федерация',
    digital_code: '643',
}

export const getNextOrdinalNumber = (positions: ContractPosition[]): number => {
    if (!positions.length) return 1
    return Math.max(...positions.map((p) => p.ordinal_number || 0)) + 1
}

export const ensureUniqueGuid = (
    sourceGuid: string | null | undefined,
    existing: ContractPosition[]
): string => {
    if (!sourceGuid) return ''

    const exists = existing.some((item) => item.guid === sourceGuid)
    return exists ? '' : sourceGuid
}

export const resolveGuidForCopyFromContract = (
    sourceGuid: string | null | undefined,
    existing: ContractPosition[]
): string | null => {
    if (!sourceGuid) return null

    const exists = existing.some((item) => item.guid === sourceGuid)
    return exists ? null : sourceGuid
}

export const createEmptyContractPosition = (
    contractGuid: string,
    ordinalNumber: number
): ContractPosition => {
    return {
        guid: null,
        contract_position_name: '',
        ordinal_number: ordinalNumber,
        okpd2_code: '',
        okpd2_name: '',
        type_object_purchase: null,
        country_of_origin: DEFAULT_COUNTRY_ORIGIN,
        lot_item_national_regime: null,
        registry_type: null,
        reg_number: null,
        score: null,
        country_manufacturer: DEFAULT_COUNTRY_MANUFACTURER,
        type_provision: 'NO_BUDGET_RESOURCES_FZ44',
        contract_num: null,
        contract_end_stage: null,
        target_code: null,
        type_code: null,
        amount_of_budget_funds: null,
        producer_country: null,
        impossible_to_determine_attr: false,
        okei_code: null,
        okei_name: null,
        qty: null,
        unit_price: null,
        currency_code: 'RUB',
        currency_digital_code: 643,
        currency_name: 'Российский рубль',
        exchange_rate: null,
        rub_unit_price: null,
        source_info: null,
        organization_guid: '',
        contract_guid: contractGuid,
        annotation: null,
        eis_guid: null,
        sys_created_at: null as unknown as string,
        sys_updated_at: null as unknown as string,
    }
}

export const createContractPositionFromPlanRow = (
    row: PlanPositionRow,
    contractGuid: string,
    ordinalNumber: number,
    maximumContractPrice: number | null = null,
    currency_code: string | null,
    currency_digital_code: number | null,
    currency_name: string | null,
    exchange_rate: number | null
): ContractPosition => {
    const qty =
        row.qty === null || row.qty === undefined ? null : Number(row.qty)

    const unitPrice =
        maximumContractPrice !== null
            ? qty !== null && qty > 0
                ? Math.round((maximumContractPrice / qty) * 100) / 100
                : maximumContractPrice
            : null

    const isRub = currency_digital_code === 643

    return {
        guid: null,
        contract_position_name:
            row.additional_info?.trim() || row.okpd2_name?.trim() || '',
        ordinal_number: ordinalNumber,
        okpd2_code: row.okpd2_code ?? '',
        okpd2_name: row.okpd2_name ?? '',
        type_object_purchase: row.type_object_purchase ?? null,
        country_of_origin: isRub ? DEFAULT_COUNTRY_ORIGIN : null,
        lot_item_national_regime: null,
        registry_type: null,
        reg_number: null,
        score: null,
        country_manufacturer: isRub ? DEFAULT_COUNTRY_MANUFACTURER : null,
        type_provision: 'NO_BUDGET_RESOURCES_FZ44',
        contract_num: null,
        contract_end_stage: null,
        target_code: null,
        type_code: null,
        amount_of_budget_funds: null,
        producer_country: null,
        impossible_to_determine_attr: row.impossible_to_determine_attr ?? false,
        okei_code: row.okei_code ?? null,
        okei_name: row.okei_name ?? null,
        qty: row.qty ?? null,
        unit_price: unitPrice,
        currency_code: currency_code,
        currency_digital_code: currency_digital_code,
        currency_name: currency_name,
        exchange_rate: exchange_rate,
        rub_unit_price: isRub
            ? unitPrice
            : unitPrice
              ? Math.round(unitPrice * (exchange_rate ?? 1) * 100) / 100
              : null,
        source_info: null,
        organization_guid: row.organization_guid,
        contract_guid: contractGuid,
        annotation: null,
        eis_guid: null,
        sys_created_at: null as unknown as string,
        sys_updated_at: null as unknown as string,
    }
}

export const createContractPositionFromContract = (
    source: ContractPosition,
    currentPositions: ContractPosition[],
    contractGuid: string,
    ordinalNumber: number
): ContractPosition => {
    return {
        ...source,
        guid: resolveGuidForCopyFromContract(source.guid, currentPositions),
        eis_guid: null,
        ordinal_number: ordinalNumber,
        contract_guid: contractGuid,
        sys_created_at: null as unknown as string,
        sys_updated_at: null as unknown as string,
    }
}

export const createContractPositionFromPurchaseLotItem = (
    lotItem: PurchaseLotItem,
    planRow: PlanPositionRow | null,
    contractGuid: string,
    ordinalNumber: number,
    currencyCode: string | null,
    currencyDigitalCode: number | null,
    currencyName: string | null,
    exchangeRate: number | null,
    maximumContractPrice: number | null = null
): ContractPosition => {
    const isRub = Number(currencyDigitalCode) === 643

    const qty =
        lotItem.qty === null || lotItem.qty === undefined
            ? null
            : Number(lotItem.qty)

    const unitPrice =
        maximumContractPrice !== null
            ? qty !== null && qty > 0
                ? Math.round((maximumContractPrice / qty) * 100) / 100
                : maximumContractPrice
            : null

    return {
        guid: null,

        contract_position_name:
            lotItem.additional_info?.trim() || lotItem.okpd2_name?.trim() || '',

        ordinal_number: ordinalNumber,

        okpd2_code: lotItem.okpd2_code ?? '',
        okpd2_name: lotItem.okpd2_name ?? '',

        type_object_purchase: planRow?.type_object_purchase ?? null,

        country_of_origin: isRub ? DEFAULT_COUNTRY_ORIGIN : null,
        lot_item_national_regime: null,
        registry_type: null,
        reg_number: null,
        score: null,
        country_manufacturer: isRub ? DEFAULT_COUNTRY_MANUFACTURER : null,

        type_provision: 'NO_BUDGET_RESOURCES_FZ44',
        contract_num: null,
        contract_end_stage: null,
        target_code: null,
        type_code: null,
        amount_of_budget_funds: null,
        producer_country: null,

        impossible_to_determine_attr:
            planRow?.impossible_to_determine_attr ?? false,

        okei_code: lotItem.okei_code ?? null,
        okei_name: lotItem.okei_name ?? null,
        qty:
            lotItem.qty === null || lotItem.qty === undefined
                ? null
                : Number(lotItem.qty),

        unit_price: unitPrice,

        currency_code: currencyCode,
        currency_digital_code: currencyDigitalCode,
        currency_name: currencyName,
        exchange_rate: exchangeRate,

        rub_unit_price: isRub
            ? unitPrice
            : unitPrice
              ? Math.round(unitPrice * (exchangeRate ?? 1) * 100) / 100
              : null,

        source_info: null,
        organization_guid: lotItem.organization_guid,
        contract_guid: contractGuid,
        annotation: null,
        eis_guid: null,
        sys_created_at: null as unknown as string,
        sys_updated_at: null as unknown as string,
    }
}
